import React, { useEffect, useState, ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Select, { SingleValue, MultiValue } from "react-select";
import { RootState } from "../redux/store";
import { useSelector } from "../redux/hooks";

const Container = styled.div`
  max-width: 700px;
  margin: 40px auto;
  padding: 20px;
  border-radius: 12px;
  background: #fff;
  font-family: "IBM Plex Mono", monospace;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const BackButton = styled.button`
  margin-bottom: 20px;
  background: #a13b16;
  color: white;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #8b2f0f;
  }
`;

const Title = styled.h2`
  color: #a13b16;
  font-size: 28px;
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-top: 15px;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-top: 6px;
  border: 1px solid #ccc;
  border-radius: 8px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  margin-top: 6px;
  border: 1px solid #ccc;
  border-radius: 8px;
  min-height: 80px;
  resize: vertical;
`;

const ImgPreview = styled.img`
  max-width: 200px;
  max-height: 200px;
  margin-top: 10px;
  border-radius: 8px;
  object-fit: contain;
  border: 1px solid #ccc;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
`;

const Button = styled.button<{ danger?: boolean }>`
  flex: 1;
  padding: 12px;
  background: ${({ danger }) => (danger ? "#d9534f" : "#a13b16")};
  color: white;
  font-size: 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: ${({ danger }) => (danger ? "#c9302c" : "#8b2f0f")};
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
`;

const FullWidthColumn = styled.div`
  grid-column: 1 / -1;
`;

const OneLineControl = styled.div`
  display: flex;
  align-items: center;
  justify-items: start;
  gap: 10px;
  height: fit-content;
  input[type="checkbox"] {
    margin-top: 15px;
  }
`;


interface Option {
  value: number;
  label: string;
}

interface BookFormData {
  book_title: string;
  author_id?: number;
  book_description?: string;
  book_quantity?: number | "";
  book_price?: number | "";
  book_page_count?: number | "";
  book_publication_year?: number | "";
  book_cover_file?: File | null;
  publisher_id?: number;
  cover_type_id?: number;
  format_id?: number;
  language_id?: number;
  genres: number[];
  is_new: boolean;
}

const BookFormView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<BookFormData>({
    book_title: "",
    author_id: undefined,
    book_description: "",
    book_quantity: "",
    book_price: "",
    book_page_count: "",
    book_publication_year: "",
    book_cover_file: null,
    publisher_id: undefined,
    cover_type_id: undefined,
    format_id: undefined,
    language_id: undefined,
    genres: [],
    is_new: false
  });

  const [coverPreview, setCoverPreview] = useState<string>("");

  const [authors, setAuthors] = useState<Option[]>([]);
  const [publishers, setPublishers] = useState<Option[]>([]);
  const [coverTypes, setCoverTypes] = useState<Option[]>([]);
  const [formats, setFormats] = useState<Option[]>([]);
  const [languages, setLanguages] = useState<Option[]>([]);
  const [genres, setGenres] = useState<Option[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [authorsRes, publishersRes, coverTypesRes, formatsRes, languagesRes, genresRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}/authors`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/cover-types`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/formats`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/languages`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/genres`),
        ]);

        const authorsData = await authorsRes.json();
        const publishersData = await publishersRes.json();
        const coverTypesData = await coverTypesRes.json();
        const formatsData = await formatsRes.json();
        const languagesData = await languagesRes.json();
        const genresData = await genresRes.json();

        setAuthors(authorsData.map((a: any) => ({ value: a.author_id, label: a.author_name })));
        setPublishers(publishersData.map((p: any) => ({ value: p.publisher_id, label: p.name })));
        setCoverTypes(coverTypesData.map((c: any) => ({ value: c.cover_type_id, label: c.type })));
        setFormats(formatsData.map((f: any) => ({ value: f.format_id, label: f.format })));
        setLanguages(languagesData.map((l: any) => ({ value: l.language_id, label: l.name })));
        setGenres(genresData.map((g: any) => ({ value: g.genre_id, label: g.genre_name })));
      } catch {
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_BASE_API_URL}/books/${id}`)
        .then((res) => res.json())
        .then((data) => {
          setFormData({
            book_title: data.book_title || "",
            author_id: data.author_id,
            book_description: data.book_description || "",
            book_quantity: data.book_quantity ?? "",
            book_price: data.book_price ?? "",
            book_page_count: data.book_page_count ?? "",
            book_publication_year: data.book_publication_year ?? "",
            book_cover_file: null,
            publisher_id: data.publisher_id,
            cover_type_id: data.cover_type_id,
            format_id: data.format_id,
            language_id: data.language_id,
            genres: data.genre_ids || [],
            is_new: data.is_new || false
          });
          if (data.book_cover_path) {
            setCoverPreview(data.book_cover_path);
          }
        });
    }
  }, [id]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "book_price" || name === "book_quantity" || name === "book_page_count" || name === "book_publication_year"
          ? value === "" ? "" : parseFloat(value)
          : value,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, book_cover_file: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectChange =
    (field: keyof BookFormData) =>
    (option: SingleValue<Option>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: option ? option.value : undefined,
      }));
    };

  const handleGenresChange = (options: MultiValue<Option>) => {
    setFormData((prev) => ({
      ...prev,
      genres: options.map((o) => o.value),
    }));
  };

  const handleCheckboxChange =
  (e: ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: e.target.checked
    })
  };

  const isFormValid = () => {
    return (
      formData.book_title.trim() !== "" &&
      formData.author_id !== undefined &&
      formData.publisher_id !== undefined &&
      formData.cover_type_id !== undefined &&
      formData.format_id !== undefined &&
      formData.language_id !== undefined &&
      formData.book_quantity !== "" && formData.book_quantity! >= 0 &&
      formData.book_price !== "" && formData.book_price! >= 0 &&
      formData.genres.length > 0
    );
  };

  const handleSubmit = async () => {
    const method = id ? "PUT" : "POST";
    const url = id
      ? `${process.env.REACT_APP_BASE_API_URL}/books/${id}`
      : `${process.env.REACT_APP_BASE_API_URL}/books`;

    const formPayload = new FormData();
    formPayload.append("book_title", formData.book_title);
    if (formData.author_id) formPayload.append("author_id", formData.author_id.toString());
    if (formData.book_description) formPayload.append("book_description", formData.book_description);
    formPayload.append("book_quantity", (formData.book_quantity ?? 0).toString());
    formPayload.append("book_price", (formData.book_price ?? 0).toString());
    if (formData.book_page_count !== "") formPayload.append("book_page_count", (formData.book_page_count ?? 0).toString());
    if (formData.book_publication_year !== "") formPayload.append("book_publication_year", (formData.book_publication_year ?? 0).toString());
    if (formData.book_cover_file) formPayload.append("book_cover_path", formData.book_cover_file);
    if (formData.publisher_id) formPayload.append("publisher_id", formData.publisher_id.toString());
    if (formData.cover_type_id) formPayload.append("cover_type_id", formData.cover_type_id.toString());
    if (formData.format_id) formPayload.append("format_id", formData.format_id.toString());
    if (formData.language_id) formPayload.append("language_id", formData.language_id.toString());
    formPayload.append("is_new", formData.is_new.toString());
    formData.genres.forEach((gid) => formPayload.append("genre_ids[]", gid.toString()));

    const res = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formPayload,
    });

    if (res.ok) {
      navigate("/admin/books");
    } else {
      alert("Не вдалося зберегти книгу");
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm("Ви дійсно хочете видалити книгу?")) return;

    const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/books/${id}`, {
      method: "DELETE",
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      }
    });
    if (res.ok) {
      navigate("/admin/books");
    } else {
      alert("Не вдалося видалити книгу");
    }
  };

  const selectedAuthor = authors.find((a) => a.value === formData.author_id) || null;
  const selectedPublisher = publishers.find((p) => p.value === formData.publisher_id) || null;
  const selectedCoverType = coverTypes.find((c) => c.value === formData.cover_type_id) || null;
  const selectedFormat = formats.find((f) => f.value === formData.format_id) || null;
  const selectedLanguage = languages.find((l) => l.value === formData.language_id) || null;
  const selectedGenres = genres.filter((g) => formData.genres.includes(g.value));

  const currentYear = new Date().getFullYear();

  return (
    <Container>
      <BackButton onClick={() => navigate("/admin/books")}>⬅ Назад</BackButton>

      <Title>{id ? "Редагування книги" : "Створення книги"}</Title>

      <Label>Назва книги</Label>
      <Input
        name="book_title"
        value={formData.book_title}
        onChange={handleInputChange}
        placeholder="Введіть назву книги"
      />

      <Label>Опис книги</Label>
      <Textarea
        name="book_description"
        value={formData.book_description}
        onChange={handleInputChange}
        placeholder="Введіть опис книги (необов'язково)"
      />

      <TwoColumnGrid>
        <div>
          <Label>Автор</Label>
          <Select
            options={authors}
            value={selectedAuthor}
            onChange={handleSelectChange("author_id")}
            isClearable
            placeholder="Оберіть автора..."
          />
        </div>

        <div>
          <Label>Видавництво</Label>
          <Select
            options={publishers}
            value={selectedPublisher}
            onChange={handleSelectChange("publisher_id")}
            isClearable
            placeholder="Оберіть видавництво..."
          />
        </div>

        <div>
          <Label>Тип обкладинки</Label>
          <Select
            options={coverTypes}
            value={selectedCoverType}
            onChange={handleSelectChange("cover_type_id")}
            isClearable
            placeholder="Оберіть тип обкладинки..."
          />
        </div>

        <div>
          <Label>Формат</Label>
          <Select
            options={formats}
            value={selectedFormat}
            onChange={handleSelectChange("format_id")}
            isClearable
            placeholder="Оберіть формат..."
          />
        </div>

        <div>
          <Label>Мова</Label>
          <Select
            options={languages}
            value={selectedLanguage}
            onChange={handleSelectChange("language_id")}
            isClearable
            placeholder="Оберіть мову..."
          />
        </div>

        <div>
          <Label>Кількість сторінок</Label>
          <Input
            name="book_page_count"
            type="number"
            placeholder="Введіть кількість сторінок"
            min={1}
            value={formData.book_page_count ?? ""}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <Label>Рік видання</Label>
          <Input
            name="book_publication_year"
            type="number"
            placeholder="Введіть рік видання"
            min={1000}
            max={currentYear}
            value={formData.book_publication_year ?? ""}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <Label>Кількість примірників</Label>
          <Input
            name="book_quantity"
            type="number"
            placeholder="Введіть кількість"
            min={0}
            value={formData.book_quantity ?? ""}
            onChange={handleInputChange}
          />
        </div>

        <div>
          <Label>Ціна (₴)</Label>
          <Input
            placeholder="Введіть ціну в гривнях"
            name="book_price"
            type="number"
            step="0.01"
            min={0}
            value={formData.book_price ?? ""}
            onChange={handleInputChange}
          />
        </div>

        <OneLineControl>
          <Label htmlFor="is-new">Новинка{formData.book_quantity === 0 && <small>(недостатня кількість)</small>}</Label>
          <input
            name="is_new"
            type="checkbox"
            value="true"
            checked={formData.is_new}
            onChange={handleCheckboxChange}
            id="is-new"
            disabled={!formData.book_quantity}
          />
        </OneLineControl>
      </TwoColumnGrid>

      <FullWidthColumn>
        <Label>Обкладинка книги</Label>
        <Input type="file" accept="image/*" onChange={handleFileChange} />
        {coverPreview && <ImgPreview src={coverPreview} alt="Прев'ю обкладинки" />}
      </FullWidthColumn>

      <FullWidthColumn>
        <Label>Жанри</Label>
        <Select
          options={genres}
          value={selectedGenres}
          onChange={handleGenresChange}
          isMulti
          placeholder="Оберіть жанри..."
        />
      </FullWidthColumn>

      <ButtonGroup>
        <Button onClick={handleSubmit} disabled={!isFormValid()}>
          {id ? "Зберегти зміни" : "Створити книгу"}
        </Button>
        {id && (
          <Button danger onClick={handleDelete}>
            Видалити книгу
          </Button>
        )}
      </ButtonGroup>
    </Container>
  );
};

export default BookFormView;